/**
 * The in-raid zone map's geometry — the pure half of `apps/client/src/ui/zonemap.ts`.
 *
 * Everything here is arithmetic and data: no DOM, no canvas, no Three. It lives in the shared
 * package for the same reason the sim does — so the one function that turns a world position into
 * a pixel can be unit tested (`packages/shared/test/zonemap.test.ts`) rather than eyeballed in a
 * screenshot. The client owns every pixel it draws; this file only says *where*.
 *
 * Nothing in here is authoritative and nothing crosses the wire. It is map data plus a transform.
 */

/** An XZ rectangle in world space. Structurally the same shape as `maps/kit.ts`'s `Rect`. */
export interface ZoneRect {
  x0: number;
  x1: number;
  z0: number;
  z1: number;
}

/**
 * The Spine's map extent, and a contract with `tools/assetgen/blender/spine_region.py`'s
 * `PLATE_RECT`: the top-down plate (`assets/gen/region/spine_map.webp`, 2048 x 1312) is rendered
 * over exactly this rect, so drawing the plate into `zoneFit()`'s box puts every pixel of it on
 * the metre it came from.
 *
 * It is the heightfield GRID's extent (67 x 43 cells of 4 m from the origin), which is one cell
 * wider and deeper than the sample extent the terrain actually spans (-132..132 / -84..84). That
 * last 4 m strip on the east and south is empty in the plate (film alpha) and empty in the world.
 */
export const SPINE_ZONE: ZoneRect = { x0: -132, x1: 136, z0: -84, z1: 88 };

/**
 * Lanternfall's map extent, and a contract with `tools/assetgen/blender/lanternfall_region.py`'s
 * plate camera: `lanternfall_map.webp` (2048 x 2048) is rendered over exactly this rect, which is
 * `LANTERNFALL_GRID`'s extent - the 120 m walled square plus one 4 m cell of margin on every side
 * so the wall itself is not clipped by the frame.
 */
export const LANTERNFALL_ZONE: ZoneRect = { x0: -64, x1: 64, z0: -64, z1: 64 };

/**
 * The Vigil Downs' map extent, and the same contract with `downs_region.py`:
 * `vigil-downs_map.webp` (2048 x 2048) covers exactly this rect, which is `DOWNS_GRID`'s extent -
 * 512 m square from (-256, -256), a hair wider than the 500 m of ground content inside it.
 */
export const DOWNS_ZONE: ZoneRect = { x0: -256, x1: 256, z0: -256, z1: 256 };

/** Grid spacing drawn over the plate, in metres. A 20 m square is about a five-second sprint. */
export const ZONE_GRID_M = 20;

/** Zoom range for the wheel/pinch. 1 fits the zone; 3 is close enough to read the basin floor. */
export const ZONE_ZOOM_MIN = 1;
export const ZONE_ZOOM_MAX = 3;

/**
 * Everything `zoneToMap` needs. `rect` is required rather than defaulted because the interior
 * view fits a deck instead of the zone, and under `exactOptionalPropertyTypes` an optional field
 * you may want to leave out is more trouble at every call site than one extra property.
 */
export interface ZoneView {
  /** Drawing surface size in CSS px. */
  w: number;
  h: number;
  /** The world rect being fitted. */
  rect: ZoneRect;
  /** 1 = the whole rect fits (letterboxed); see ZONE_ZOOM_MIN/MAX. */
  zoom: number;
  /** Pan in CSS px, 0/0 = centred. Pass what `clampZonePan` returned. */
  panX: number;
  panY: number;
}

/** Where `view.rect` lands on the drawing surface. */
export interface ZoneFit {
  /** CSS px per world metre. */
  scale: number;
  /** Top-left corner of the fitted rect, in CSS px. */
  x: number;
  y: number;
  /** Size of the fitted rect, in CSS px. */
  w: number;
  h: number;
}

/**
 * Letterbox `view.rect` into `view.w` x `view.h`, scaled by `view.zoom` and shifted by the pan.
 *
 * The aspect is preserved on purpose: the Spine is 268 x 172 m, and a map that stretches to the
 * panel would put the rim closer on one axis than the other and quietly lie about every distance
 * a player reads off it.
 */
export function zoneFit(view: ZoneView): ZoneFit {
  const rw = view.rect.x1 - view.rect.x0;
  const rh = view.rect.z1 - view.rect.z0;
  // A zero-sized surface happens for one frame between `display:none` and the first layout, and
  // an Infinity scale would poison every marker position downstream.
  if (rw <= 0 || rh <= 0 || view.w <= 0 || view.h <= 0) {
    return { scale: 0, x: 0, y: 0, w: 0, h: 0 };
  }
  const scale = Math.min(view.w / rw, view.h / rh) * view.zoom;
  const w = rw * scale;
  const h = rh * scale;
  return { scale, x: (view.w - w) / 2 + view.panX, y: (view.h - h) / 2 + view.panY, w, h };
}

/**
 * A world XZ position to a pixel on the map. North (-Z) is up, east (+X) is right — the same way
 * up as the plate, the overview render and every compass bearing in the HUD.
 */
export function zoneToMap(x: number, z: number, view: ZoneView): { x: number; y: number } {
  const fit = zoneFit(view);
  return { x: fit.x + (x - view.rect.x0) * fit.scale, y: fit.y + (z - view.rect.z0) * fit.scale };
}

/** The inverse: a pixel back to world XZ. Used by hover, by drag-pan and by zoom-about-cursor. */
export function mapToZone(px: number, py: number, view: ZoneView): { x: number; z: number } {
  const fit = zoneFit(view);
  if (fit.scale <= 0) return { x: view.rect.x0, z: view.rect.z0 };
  return { x: view.rect.x0 + (px - fit.x) / fit.scale, z: view.rect.z0 + (py - fit.y) / fit.scale };
}

/**
 * Pan clamped so the fitted rect never pulls away from the surface: at zoom 1 it is pinned dead
 * centre (both limits are 0), and past that it may slide until an edge reaches the border.
 */
export function clampZonePan(view: ZoneView): { panX: number; panY: number } {
  const centred = zoneFit({ ...view, panX: 0, panY: 0 });
  const limX = Math.max(0, (centred.w - view.w) / 2);
  const limY = Math.max(0, (centred.h - view.h) / 2);
  return {
    panX: Math.min(limX, Math.max(-limX, view.panX)),
    panY: Math.min(limY, Math.max(-limY, view.panY)),
  };
}

/**
 * Re-zoom about a fixed pixel: the world point under (px, py) is still under it afterwards, which
 * is the whole feel of a wheel zoom. Returns the clamped view, so a zoom that would have pushed
 * the plate off the surface simply pulls it back instead.
 */
export function zoneZoomAbout(view: ZoneView, zoom: number, px: number, py: number): ZoneView {
  const next = Math.min(ZONE_ZOOM_MAX, Math.max(ZONE_ZOOM_MIN, zoom));
  const world = mapToZone(px, py, view);
  const unpanned = { ...view, zoom: next, panX: 0, panY: 0 };
  const at = zoneToMap(world.x, world.z, unpanned);
  const panned = { ...unpanned, panX: px - at.x, panY: py - at.y };
  return { ...panned, ...clampZonePan(panned) };
}

// ---------------------------------------------------------------------------------------------
// The Spine's named ground
// ---------------------------------------------------------------------------------------------

/** A place name printed on the map, anchored at the middle of the ground it names. */
export interface ZoneArea {
  name: string;
  x: number;
  z: number;
}

/**
 * The eight areas of the Spine, as `docs/ART-DIRECTION.md` names them. Anchors are the landmark
 * points `spine_region.py` writes into the sidecar (`landmarks`), with the combined labels split
 * back into the single names a player would say out loud.
 */
export const SPINE_ZONE_AREAS: readonly ZoneArea[] = [
  { name: 'Wreck Basin', x: 0, z: 50 },
  { name: 'Approach Dunes', x: 0, z: 74 },
  { name: 'West Rim', x: -72, z: 0 },
  { name: 'Dry Channel', x: -88, z: 31 },
  { name: 'Pylon Field', x: -105, z: -30 },
  { name: 'North Ridge', x: 0, z: -66 },
  // South-east of the sidecar's landmark point (82, 15): that sits on the Ferry Pickup extract,
  // and on a phone the area name and the extract's own label printed on top of each other.
  { name: 'Antenna Knoll', x: 92, z: 44 },
  { name: 'Glass Outcrop', x: 108, z: -12 },
];

/** The four places worth printing on Lanternfall's plan. Anchors are `lanternfall.ts`'s own. */
export const LANTERNFALL_ZONE_AREAS: readonly ZoneArea[] = [
  { name: 'the Gate', x: 0, z: -56 },
  { name: 'the Square', x: 0, z: 4 },
  { name: 'the Lantern Hall', x: 0, z: 44 },
  { name: "Sabel's Counter", x: 45, z: -5 },
  { name: 'the Graveyard', x: -46, z: 45 },
];

/**
 * The Vigil Downs' six named areas plus the wayshrine, in the exact words `vigil-downs.ts`'s
 * `AreaDef`s use - the quest library resolves `areaHint` against those strings, so a label here
 * that drifts from one there is a label pointing at ground no objective can name.
 */
export const DOWNS_ZONE_AREAS: readonly ZoneArea[] = [
  { name: 'the Lantern Road', x: 0, z: 130 },
  { name: 'the Tithe Barrows', x: 150, z: 60 },
  { name: 'the Choir Stones', x: 0, z: -80 },
  { name: 'the Drowned Chapel', x: -160, z: 20 },
  { name: 'the Hollow Orchard', x: -150, z: 150 },
  { name: 'the Watch Hill', x: 180, z: -180 },
  { name: 'the Wayshrine', x: 18, z: 40 },
];

/** Everything the M-key map needs about one map: its extent, its plate and its printed names. */
export interface ZoneMapDef {
  rect: ZoneRect;
  /** Top-down plate under the same rect, or null for a map with no plate rendered yet. */
  plate: string | null;
  areas: readonly ZoneArea[];
}

/**
 * The map the M key draws for a given `MapDef.id`, or null for one with no plan (a test yard).
 * One table rather than a chain of `if (mapId === …)` at every call site, so adding a zone is a
 * row here plus a Blender plate.
 */
export function zoneMapFor(mapId: string): ZoneMapDef | null {
  switch (mapId) {
    case 'spine':
      return {
        rect: SPINE_ZONE,
        plate: '/assets/gen/region/spine_map.webp',
        areas: SPINE_ZONE_AREAS,
      };
    case 'lanternfall':
      return {
        rect: LANTERNFALL_ZONE,
        plate: '/assets/gen/region/lanternfall_map.webp',
        areas: LANTERNFALL_ZONE_AREAS,
      };
    case 'vigil-downs':
      return {
        rect: DOWNS_ZONE,
        plate: '/assets/gen/region/vigil-downs_map.webp',
        areas: DOWNS_ZONE_AREAS,
      };
    default:
      return null;
  }
}

// ---------------------------------------------------------------------------------------------
// The roaming Verger's announced ground
// ---------------------------------------------------------------------------------------------

/**
 * "The Choir has sent its Verger. It is walking ___." — the room's one-off line when the director
 * rolls a roaming boss (`apps/server/src/game/expedition.ts`).
 *
 * It arrives as an ordinary unattributed `S2C.Chat`; there is no protocol field for it and no
 * position, deliberately (a rumour is not a marker). So the map parses the sentence and hatches
 * the ground it names, which is exactly the fidelity the line itself has.
 */
export const VERGER_NOTICE_PREFIX = 'The Choir has sent its Verger. It is walking ';

/** One announceable region: the phrase, and roughly the ground it covers. */
export interface ZoneRoamRegion extends ZoneRect {
  /** Verbatim second clause, matching `DirectorBucket.walking` in expedition.ts. */
  walking: string;
  /** What the map's tooltip calls it. */
  name: string;
}

/**
 * The six director buckets of the Spine, as areas rather than as centres.
 *
 * The server's buckets are points with no boundary geometry (a waypoint node belongs to the
 * nearest one), so these rects are an honest approximation drawn around those centres — they are
 * hatched shading on a map, never a hit test. Drift only ever costs the hatch: a phrase this
 * table does not know returns null and the map says nothing, which is the right failure.
 */
export const SPINE_ROAM_REGIONS: readonly ZoneRoamRegion[] = [
  { walking: 'the basin', name: 'Wreck Basin', x0: -40, x1: 40, z0: -12, z1: 52 },
  { walking: 'the dunes', name: 'Approach Dunes', x0: -46, x1: 46, z0: 40, z1: 88 },
  { walking: 'the dry channel', name: 'West Rim / Dry Channel', x0: -132, x1: -52, z0: -6, z1: 66 },
  { walking: 'the pylon field', name: 'Pylon Field', x0: -132, x1: -58, z0: -84, z1: -8 },
  { walking: 'the north ridge', name: 'North Ridge', x0: -48, x1: 48, z0: -84, z1: -12 },
  { walking: 'the glass flats', name: 'Antenna Knoll / Glass', x0: 46, x1: 136, z0: -34, z1: 58 },
];

/**
 * The region a chat line announces, or null for any other line. Tolerant of the trailing full
 * stop and of surrounding whitespace, strict about everything else: a line that is not the
 * Verger's must never light up ground.
 */
export function roamRegionForNotice(text: string): ZoneRoamRegion | null {
  const line = text.trim();
  if (!line.startsWith(VERGER_NOTICE_PREFIX)) return null;
  const clause = line.slice(VERGER_NOTICE_PREFIX.length).replace(/\.+$/, '').trim();
  return SPINE_ROAM_REGIONS.find((r) => r.walking === clause) ?? null;
}

// ---------------------------------------------------------------------------------------------
// The wreck's decks
// ---------------------------------------------------------------------------------------------

/**
 * The three decks' shared footprint (`maps/spine.ts`'s HULL_X0..HULL_Z1) and the flattened apron
 * around it. `SPINE_APRON_RECT` is the wider one — the ground the terrain is levelled over, and
 * the footprint the map uses to decide "this player is inside the wreck, show them the deck plan"
 * (a player on the dune crest at x = 32 is at the east breach, which is interior business).
 */
export const SPINE_HULL_RECT: ZoneRect = { x0: -25, x1: 25, z0: 6, z1: 34 };
export const SPINE_APRON_RECT: ZoneRect = { x0: -27, x1: 36, z0: 2, z1: 38 };

/** Black-drop pit under Deck 0's floor: a hole to a floor slab 6 m down. */
export const SPINE_PIT_RECT: ZoneRect = { x0: -6, x1: 6, z0: 15, z1: 25 };
/** Deck 0 -> Deck 1 collapsed-floor mantle chain (west side). */
export const SPINE_CHAIN_RECT: ZoneRect = { x0: -24, x1: -16, z0: 9, z1: 17 };
/** Deck 0 -> Deck 1 walkable stair, a second route (west side, south of the chain). */
export const SPINE_STAIR_RECT: ZoneRect = { x0: -24, x1: -16, z0: 19, z1: 31 };
/** Deck 1 -> Deck 2 lift shaft: mantle ledges on the north half, an open drop on the south half. */
export const SPINE_SHAFT_RECT: ZoneRect = { x0: 16, x1: 24, z0: 16, z1: 24 };
/** Deck 2 roof hatch above the internal ridge stair onto the dorsal ridge. */
export const SPINE_RIDGE_HATCH_RECT: ZoneRect = { x0: -3, x1: 3, z0: 6, z1: 14 };

/** One deck band of the wreck, as the map's tab strip flips through them. */
export interface ZoneDeck {
  /** Tab label. */
  tab: string;
  /** What the deck is called in the fiction — the strip's second line and the tooltip. */
  name: string;
  /**
   * Half-open y band [y0, y1): which deck a player's FEET are standing on. Deck 0's band reaches
   * below its floor because the black-drop pit's floor is 6 m under it and a player down there is
   * still in the Cargo Hold.
   */
  y0: number;
  y1: number;
  /**
   * The walking surface itself, and the underside of the deck above it. A deck plan wants the
   * things standing *between* those two — walls, crates, pillars, partitions — and not the floor
   * slab (whose top is flush with `floorY`) or the ceiling (whose underside is at `headY`), both
   * of which would print as one filled rectangle over the whole footprint.
   */
  floorY: number;
  headY: number;
}

/**
 * The four levels the interior view flips between. The y numbers are `maps/spine.ts`'s
 * DECK0_BASE / DECK1_BASE / DECK2_BASE / RIDGE_Y; the test pins them to those exports so a
 * change to the deck heights cannot leave the map reading the wrong floor.
 */
export const SPINE_DECKS: readonly ZoneDeck[] = [
  { tab: 'Deck 0', name: 'Cargo Hold', y0: -8, y1: 8, floorY: 0, headY: 8 },
  { tab: 'Deck 1', name: 'Habitation Ring', y0: 8, y1: 14, floorY: 8, headY: 14 },
  { tab: 'Deck 2', name: 'Choir Deck', y0: 14, y1: 20.4, floorY: 14, headY: 20 },
  { tab: 'Roof', name: 'Dorsal Ridge', y0: 20.4, y1: 200, floorY: 20.4, headY: 26 },
];

/**
 * How far a box's body has to reach past a deck's floor and under its ceiling to count as
 * something on that deck's plan. Half a metre: enough to drop a 0.4 m slab, small enough to keep
 * an anchor pad's 0.2 m lip out and a crate in.
 */
export const SPINE_DECK_SLAB_M = 0.5;

/** Which deck a y sits on. Below the pit floor and above the ridge both clamp into range. */
export function spineDeckAt(y: number): number {
  for (let i = SPINE_DECKS.length - 1; i >= 0; i--) {
    const deck = SPINE_DECKS[i];
    if (deck && y >= deck.y0) return i;
  }
  return 0;
}

/** True when (x, z) is over the wreck — the trigger for the map's interior view. */
export function inSpineHull(x: number, z: number): boolean {
  const r = SPINE_APRON_RECT;
  return x >= r.x0 && x <= r.x1 && z >= r.z0 && z <= r.z1;
}
